//deps
import React, { memo, useCallback, useMemo } from "react";

//components
import { Radio } from "@swiggy-private/dls-web-core/dist/components/RadioGroup";
import DeliveryOption from "./Option";
import { SlaComms } from "@cart-ui/components/sla-comms";
import { DeliverySpeedOptinWidget } from "@cart-ui/components/delivery-speed/components/delivery-speed-optin-widget";
import EcoSaverLottie from "./components/EcoSaverLottie/EcoSaverLottie";
import { DeliveryOptionAsBanner } from "@cart-ui/components/delivery-option-as-banner";

//helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { computeShouldRenderDeliveryOptionBanner } from "./helpers";

//styles
import { SlaCommsWrapper, Wrapper } from "./styles";

//types and interfaces
import { DeliverySpeedOptionProps } from "./types";
import { DELIVERY_SPEED, Option } from "@swiggy-private/cart-types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

function DeliverySpeedOption({
    options,
    onChange,
    slaComms,
    optInText,
    onOptInWidgetClick,
    deliveryTypeOptIn,
    selectedSlaOption,
    onOptInWidgetViewEnter,
    onSlaCommsViewEnter,
    setShowM2ChalfcardOnClick,
    impressionExpressBuyBlckNudge,
    clickExpressBuyBlckNudge,
    onOfferTimerEnds,
    impressionDeliveryTypeOffer,
    impressionDeliveryTypeOfferExpired,
    impressionDeliverySpeedCallout,
    ecoSaverLottieSuccess,
}: DeliverySpeedOptionProps) {
    const suppressOptionTopBorder = useMemo(() => options.some((item) => !isEmpty(item.callout)), [options]);

    const Options = useCallback(
        (item: Option, index: number) => (
            <DeliveryOption
                key={index}
                selected={selectedSlaOption === item.option}
                heading={item.heading}
                subHeading={item.subHeading}
                headingInfo={item.headingInfo}
                isDisabled={item.isDisabled}
                meta={item.meta}
                slaData={item.slaData}
                option={item.option}
                isLastOption={index === options.length - 1}
                isFirstOption={index === 0}
                bannerData={item?.bannerData}
                onClick={() => onChange(item)}
                setShowM2ChalfcardOnClick={setShowM2ChalfcardOnClick}
                impressionExpressBuyBlckNudge={impressionExpressBuyBlckNudge}
                clickExpressBuyBlckNudge={clickExpressBuyBlckNudge}
                offerInfo={item?.offerInfo}
                onOfferTimerEnds={onOfferTimerEnds}
                impressionDeliveryTypeOffer={impressionDeliveryTypeOffer}
                impressionDeliveryTypeOfferExpired={impressionDeliveryTypeOfferExpired}
                impressionDeliverySpeedCallout={impressionDeliverySpeedCallout}
                deliverySpeedWithIcon={item.deliverySpeedWithIcon}
                priceInfo={item.priceInfo}
                callout={item.callout}
                suppressOptionTopBorder={suppressOptionTopBorder}
            />
        ),
        [
            selectedSlaOption,
            options,
            suppressOptionTopBorder,
            setShowM2ChalfcardOnClick,
            impressionExpressBuyBlckNudge,
            clickExpressBuyBlckNudge,
            onOfferTimerEnds,
            impressionDeliveryTypeOffer,
            impressionDeliveryTypeOfferExpired,
            impressionDeliverySpeedCallout,
            onChange,
        ],
    );

    const renderDeliveryOptionRadioButton = useCallback(
        (item: Option, index: number) => {
            return (
                <Wrapper key={index} isFirstOption={index === 0} isLastOption={index === options.length - 1}>
                    {item.option === DELIVERY_SPEED.BATCH_NO_RUSH && ecoSaverLottieSuccess && (
                        <EcoSaverLottie {...ecoSaverLottieSuccess} />
                    )}
                    <Radio
                        label={item.heading}
                        id={item.option}
                        value={item.option}
                        orientation="ltr"
                        Comp={() => Options(item, index)}
                        key={index}
                        onChange={() => onChange(item)}
                        name="delivery-type-radio"
                        checked={selectedSlaOption === item.option}
                        isDisabled={item.isDisabled}
                        aria-hidden="true"
                        color={item.meta?.highlightColor as keyof IColors}
                    />
                </Wrapper>
            );
        },
        [options.length, ecoSaverLottieSuccess, selectedSlaOption, Options, onChange],
    );

    const renderDeliveryOptionBanner = useCallback(
        (item: Option, index: number) => {
            return (
                <DeliveryOptionAsBanner
                    key={index}
                    deliverySpeedOnlyPresentation={item.deliverySpeedOnlyPresentation}
                    deliverySpeedOnlyPresentationIcon={item.deliverySpeedOnlyPresentationIcon}
                    slaCommsText={slaComms?.slaText}
                />
            );
        },
        [slaComms?.slaText],
    );

    const renderDeliveryOptionWrapper = useCallback(
        (item: Option, index: number) => {
            // should render banner if the item has a deliverySpeedOnlyPresentation and deliverySpeedOnlyPresentationIcon
            const renderBanner = computeShouldRenderDeliveryOptionBanner(item);
            // banner is a full bleed component that spans the width of the container instead of delivery option radio button
            const componentToRender = renderBanner ? renderDeliveryOptionBanner : renderDeliveryOptionRadioButton;
            return componentToRender(item, index);
        },
        [renderDeliveryOptionBanner, renderDeliveryOptionRadioButton],
    );

    return (
        <>
            {!isEmpty(slaComms?.slaText) && (
                <SlaCommsWrapper>
                    <SlaComms {...slaComms} onCommsVisible={onSlaCommsViewEnter} />
                </SlaCommsWrapper>
            )}
            {options.map((item, index) => renderDeliveryOptionWrapper(item, index))}
            <DeliverySpeedOptinWidget
                optInText={optInText}
                isOptedIn={Boolean(deliveryTypeOptIn)}
                selectedSlaOption={selectedSlaOption}
                onOptInWidgetClick={onOptInWidgetClick}
                onOptInWidgetViewEnter={onOptInWidgetViewEnter}
            />
        </>
    );
}

export default memo(DeliverySpeedOption);
